import { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBars,
  FaBox,
  FaChevronDown,
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaSearch,
  FaSignInAlt,
  FaSignOutAlt,
  FaTimes,
  FaUserCircle,
  FaUserCog,
} from "react-icons/fa";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { GrContact } from "react-icons/gr";

import API from "../../../utils/api";
import RenderToast from "../../RenderToast";
import SocialSection from "../../SocialSection";
import classes from "./HeaderOne.module.css";

function readStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function HeaderOne() {
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
  const [user, setUser] = useState(readStoredUser());
  const [dashOpen, setDashOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [openMenus, setOpenMenus] = useState({});
  const [allProducts, setAllProducts] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const drawerRef = useRef();
  const searchWrapRef = useRef(null);

  const normalizedSearch = search.trim().toLowerCase();
  const uploadBase = API.base?.endsWith("/techmatter_api")
    ? API.base
    : `${API.base}/techmatter_api`;

  const getProductThumb = (imageName) => {
    if (!imageName) return `${uploadBase}/upload/placeholder-image.jpg`;
    return `${uploadBase}/upload/${imageName}`;
  };

  const suggestionResults = useMemo(() => {
    if (normalizedSearch.length < 2) return [];
    return allProducts
      .filter((product) =>
        String(product.sku || "")
          .toLowerCase()
          .includes(normalizedSearch)
      )
      .slice(0, 6);
  }, [allProducts, normalizedSearch]);

  // Fetch categories
  useEffect(() => {
    setCategoriesLoading(true);
    fetch(`${API.base}/categories.php`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ action: "fetch" }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const parents = data
            .filter((c) => !c.parent_id && c.categoryName !== "Uncategorized")
            .slice(0, 5);

          const allCats = parents.map((parent) => ({
            ...parent,
            children: data.filter((c) => c.parent_id === parent.id),
          }));

          setCategories(allCats);
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setCategoriesLoading(false));
  }, []);

  // Fetch products (same as your code)
  useEffect(() => {
    fetch(`${API.base}/products.php`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({ action: "fetch" }),
    })
      .then((res) => res.json())
      .then((data) =>
        setAllProducts(Array.isArray(data) ? data : data?.data || [])
      )
      .catch((err) => console.error(err));
  }, []);

  // Suggestions open/close
  useEffect(() => {
    if (normalizedSearch.length >= 2) setSuggestionsOpen(true);
    else setSuggestionsOpen(false);
  }, [normalizedSearch]);

  useEffect(() => {
    if (!suggestionsOpen) return;
    const handleOutside = (event) => {
      if (
        searchWrapRef.current &&
        !searchWrapRef.current.contains(event.target)
      ) {
        setSuggestionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [suggestionsOpen]);

  // Storage updates
  useEffect(() => {
    const onStorage = () => {
      setAuthToken(localStorage.getItem("authToken"));
      setUser(readStoredUser());
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("userUpdated", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("userUpdated", onStorage);
    };
  }, []);

  // Drawer outside click
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        drawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(e.target)
      ) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [drawerOpen]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const q = search.trim().toLowerCase();
    if (!q) return RenderToast({ type: "error", message: "Please enter SKU" });

    const product = allProducts.find(
      (p) => p.sku?.toLowerCase() === q || p.productName?.toLowerCase() === q
    );

    if (product) {
      navigate(`/product/${product.product_slug}`);
      setSearch("");
      setSuggestionsOpen(false);
    } else {
      RenderToast({ type: "error", message: "Product not found" });
    }
  };

  const handleSuggestionSelect = (product) => {
    navigate(`/product/${product.product_slug || product.sku}`);
    setSearch("");
    setSuggestionsOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    window.dispatchEvent(new Event("userUpdated"));
    setAuthToken(null);
    setUser(null);
    setDashOpen(false);
    navigate("/");
  };

  const toggleMegaMenu = (id) => {
    setOpenMenus((prev) => {
      const isCurrentlyOpen = !!prev[id];
      if (isCurrentlyOpen) return {};
      return { [id]: true };
    });
  };

  return (
    <header className={classes.header}>
      {/* Top Row */}
      <div className={`${classes.rowFull} ${classes.topRow}`}>
        <div className={classes.rowInner}>
          <div className={classes.row1Left}>
            <FaMapMarkerAlt className={classes.icon} />
            <span className={classes.smallText}>
              F-10 Leaf house, Catherine Place, Harrow, HA1 2JW
            </span>
          </div>

          <div className={classes.row1Right}>
            <div className={classes.contactItem}>
              <FaPhoneAlt className={classes.icon} />
              <a className={classes.link} href="tel:+442031293546">
                +44 20 3129 3546
              </a>
            </div>
            <div className={classes.contactItem}>
              <FaEnvelope className={classes.icon} />
              <a className={classes.link} href="mailto:sales@techmatter.co.uk">
                sales@techmatter.co.uk
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Logo / Search / Auth */}
      <div className={classes.rowFull}>
        <div className={classes.rowInner}>
          <div className={classes.logoWrap}>
            <Link to="/" className={classes.logoLink}>
              <img
                src="/techmatter_uk_color_logo.png"
                alt="TechMatter"
                className={classes.logoImg}
              />
            </Link>
          </div>

          <div className={classes.searchWrap} ref={searchWrapRef}>
            <div className={classes.searchInner}>
              <form
                onSubmit={handleSearchSubmit}
                className={classes.searchForm}
              >
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search product SKU..."
                  className={classes.searchInput}
                  onFocus={() => {
                    if (normalizedSearch.length >= 2) {
                      setSuggestionsOpen(true);
                    }
                  }}
                />
                <button type="submit" className={classes.searchBtn}>
                  <FaSearch />
                </button>
              </form>
              {suggestionsOpen && (
                <div className={classes.searchDropdown}>
                  {suggestionResults.length === 0 ? (
                    <p className={classes.searchDropdownEmpty}>
                      No matching products
                    </p>
                  ) : (
                    suggestionResults.map((product) => (
                      <button
                        type="button"
                        key={product.id ?? product.sku}
                        className={classes.searchDropdownItem}
                        onClick={() => handleSuggestionSelect(product)}
                      >
                        <img
                          src={getProductThumb(product.productImage)}
                          alt={product.productName}
                          className={classes.searchDropdownImage}
                          onError={(e) =>
                            (e.currentTarget.src = "/placeholder-image.png")
                          }
                        />
                        <div className={classes.searchDropdownMeta}>
                          <p className={classes.searchDropdownTitle}>
                            {product.productName || "Untitled Product"}
                          </p>
                          <span className={classes.searchDropdownSku}>
                            SKU: {product.sku || "N/A"}
                          </span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={classes.authWrap}>
            {/* {authToken ? (
              <div className={classes.dashboard}>
                <button
                  className={classes.authBtn}
                  onClick={() => setDashOpen((s) => !s)}
                >
                  <FaUserCircle className={classes.iconSmall} />
                  {user?.name || "Dashboard"}
                  <FaChevronDown className={classes.chev} />
                </button>

                {dashOpen && (
                  <div className={classes.dashDropdown}>
                    <Link
                      to="/dashboard"
                      className={classes.dropdownItem}
                      onClick={() => setDashOpen(false)}
                    >
                      <FaUserCog /> Dashboard
                    </Link>
                    <button
                      className={classes.dropdownItem}
                      onClick={handleLogout}
                    >
                      <FaSignOutAlt /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                className={classes.authBtnDesktop}
                onClick={() => navigate("/contact")}
              >
                <GrContact /> Contact Us
              </button>
            )} */}

            <button
              className={classes.authBtnDesktop}
              onClick={() => navigate("/contact")}
            >
              <GrContact /> Contact Us
            </button>

            <button
              className={classes.hamburger}
              onClick={() => setDrawerOpen(true)}
            >
              <FaBars />
            </button>
          </div>
        </div>
      </div>

      {/* Categories Row */}
      <div className={classes.rowFull + " " + classes.categoriesRow}>
        <div className={classes.rowInner}>
          <ul className={classes.categories}>
            {categories.length === 0
              ? // Show skeleton placeholders while loading
                Array.from({ length: 5 }).map((_, idx) => (
                  <li key={idx} className={classes.catItem}>
                    <div className={classes.catSkeleton}></div>
                  </li>
                ))
              : // Show actual categories once data is loaded
                categories.map((cat) => (
                  <li key={cat.id} className={classes.catItem}>
                    <NavLink
                      to={`/category/${cat.category_slug}`}
                      className={classes.catLink}
                    >
                      {cat.categoryName}
                      {cat.children.length > 0 && (
                        <span
                          className={classes.chevSmall}
                          onClick={(e) => {
                            e.preventDefault();
                            toggleMegaMenu(cat.id);
                          }}
                        >
                          {openMenus[cat.id] ? <FaTimes /> : <FaChevronDown />}
                        </span>
                      )}
                    </NavLink>

                    {cat.children.length > 0 && openMenus[cat.id] && (
                      <div
                        className={`${classes.megaMenu} ${
                          cat.children.length === 1
                            ? classes.oneColumn
                            : classes.twoColumn
                        }`}
                      >
                        {cat.children.map((child) => (
                          <Link
                            key={child.id}
                            to={`/category/${child.category_slug}`}
                            className={classes.megaCard}
                          >
                            <FaBox className={classes.megaIcon} />
                            <div className={classes.megaTitle}>
                              {child.categoryName}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </li>
                ))}
          </ul>
        </div>
      </div>

      {/* ==================================== */}
      {/* 🚀 MOBILE DRAWER - ADDED & FIXED     */}
      {/* ==================================== */}

      <div
        className={`${classes.drawerOverlay} ${
          drawerOpen ? classes.drawerOpen : ""
        }`}
      >
        <div ref={drawerRef} className={classes.drawer}>
          <button
            className={classes.drawerClose}
            onClick={() => setDrawerOpen(false)}
          >
            <FaTimes />
          </button>

          {/* LOGO */}
          <div className={classes.drawerLogo}>
            <img
              src="/techmatter_uk_color_logo.png"
              alt="TechMatter Logo"
              className={classes.drawerLogoImg}
            />
          </div>

          {/* NAV LINKS */}
          <nav className={classes.drawerLinks}>
            <Link
              to="/"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Home
            </Link>

            <Link
              to="/shop"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Shop
            </Link>

            <Link
              to="/about"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              About
            </Link>

            <Link
              to="/contact"
              className={classes.drawerLink}
              onClick={() => setDrawerOpen(false)}
            >
              Contact
            </Link>
          </nav>

          <div className={classes.drawerDivider}></div>

          {/* AUTH BUTTONS */}
          {/* {authToken ? (
            <>
              <button
                className={classes.drawerItem}
                onClick={() => {
                  navigate("/dashboard");
                  setDrawerOpen(false);
                }}
              >
                <FaUserCog /> Dashboard
              </button>

              <button className={classes.drawerItem} onClick={handleLogout}>
                <FaSignOutAlt /> Logout
              </button>
            </>
          ) : (
            <>
              <button
                className={classes.drawerItem}
                onClick={() => {
                  navigate("/tech-login-478");
                  setDrawerOpen(false);
                }}
              >
                <FaSignInAlt /> Login
              </button>
            </>
          )} */}

          {/* BOTTOM SOCIAL */}
          <div className={classes.drawerBottom}>
            <SocialSection />
          </div>
        </div>
      </div>
      {/* END DRAWER */}
    </header>
  );
}
